import { useMemo } from "react"
import { useOutletContext } from "react-router-dom"
import Grid from "@mui/material/Grid"

import type { DeviceGroup } from "types/group.types"

import DeviceGroupDevicesTable from "widgets/device/DevicesTable/DeviceGroupDevicesTable"
import { BodyContainer } from "widgets/styled/containers"
import MapSection from "widgets/common/MapSection"
import useGeolocations from "helpers/hooks/useGeolocations"
import GeoJSONLayers from "widgets/plots/geo/GeoJSONLayers"
import { getMapCenterFromGeo } from "helpers/utils/map"
import { useFullDeviceRows } from "helpers/hooks/devices/useFullDeviceRows"

function GroupInfo() {
  const { deviceGroup, deviceGroupLoading } = useOutletContext<{
    deviceGroup: DeviceGroup
    deviceGroupLoading: boolean
  }>()

  const deviceList = useMemo(
    () => deviceGroup.device_list as number[],
    [deviceGroup.device_list],
  )

  // Device table

  const {
    getGroupDevicesRows,
    installedOnLoading,
    statusesLoading,
    isLoading: devicesLoading,
  } = useFullDeviceRows()

  const groupDevicesRows = useMemo(
    () => getGroupDevicesRows(deviceList),
    [getGroupDevicesRows, deviceList],
  )

  // Device map

  const { geoData, isLoading: geoLoading } = useGeolocations()

  const devicesGeo = useMemo(() => {
    if (geoData) {
      const deviceListSet = new Set(deviceList)
      const filteredFeatures = geoData?.Device.features.filter((feature) =>
        deviceListSet.has(feature.properties.id),
      )
      return { Device: { ...geoData.Device, features: filteredFeatures } }
    }
  }, [geoData, deviceList])

  const center = useMemo(() => getMapCenterFromGeo(devicesGeo), [devicesGeo])

  const shouldRenderMap = useMemo(
    () => devicesGeo?.Device?.features && devicesGeo.Device.features.length > 0,
    [devicesGeo],
  )

  return (
    <BodyContainer disableGutters maxWidth="xl">
      <Grid container spacing={2}>
        <MapSection
          center={center}
          isPending={devicesLoading || geoLoading || !devicesGeo}
          shouldRender={!!shouldRenderMap}
        >
          <GeoJSONLayers data={devicesGeo} showLayersControl={false} />
        </MapSection>
        <Grid item xs={12} sx={{ mt: "1rem" }}>
          <DeviceGroupDevicesTable
            devices={groupDevicesRows}
            installedOnLoading={installedOnLoading}
            statusesLoading={statusesLoading}
            isLoading={devicesLoading || deviceGroupLoading}
            titleKey="device_groups.GROUP_DEVICES"
            group={deviceGroup}
          />
        </Grid>
      </Grid>
    </BodyContainer>
  )
}

export default GroupInfo
